import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import buildRadarData from '../../../helpers/dataHelpers/matrixDataHelpers/buildRadarData'
import PolarQuestionTick from '../PolarQuestionTick'
import MatrixRadarTooltip from './MatrixRadarTooltip'
import AnalyticsEmptyState from '../../dashboard/AnalyticsEmptyState'
import { CATEGORICAL } from '../../../helpers/chartPalette'

// AI-CONTEXT: See docs/features/msi-effect-radar-chart.md
//
// MSI-effect radar/spider chart: one axis per row question, one overlaid shape
// per meetmoment. Fed from useMatrixData's un-collapsed `rowData` (already
// mirrored — higher = better), transposed by buildRadarData. Colours are keyed
// by MOMENT (not row), so two moments read as two shapes. Works for both MSI
// (Standard) and custom matrix fields.

// Brand-derived series colours per moment (before → after → …). Coral, dark
// teal, primary teal, then a warm/green fallback ramp for >3 moments.
const MOMENT_COLORS = CATEGORICAL

/**
 * @param {object} props
 * @param {Array<object>} props.rowData - useMatrixData `rowData` (un-collapsed, per moment).
 * @param {Array<{ID: string, Title: string, posNeg?: string}>} [props.rawRowDefs] - Real row defs, for axis ordering.
 * @param {number|string} props.lowerScale - Radius axis lower bound.
 * @param {number|string} props.upperScale - Radius axis upper bound.
 * @param {string} [props.graphColor] - Axis/label colour override.
 * @returns {JSX.Element}
 */
const MatrixRadarChart = ({ rowData, rawRowDefs, lowerScale, upperScale, graphColor }) => {
  const radarData = buildRadarData(rowData, rawRowDefs)

  // Series = one per moment, in measurement order (Voormeting → Nameting → …).
  const momentNames = (Array.isArray(rowData) ? rowData : [])
    .map((moment) => moment?.name)
    .filter((name) => typeof name === 'string')

  if (radarData.length === 0 || momentNames.length === 0) {
    return <AnalyticsEmptyState />
  }

  return (
    <ResponsiveContainer width="100%" height={500}>
      <RadarChart data={radarData} outerRadius="70%" margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
        <PolarGrid stroke="#e0e0e0" />
        <PolarAngleAxis dataKey="question" tick={<PolarQuestionTick fill={graphColor || undefined} />} />
        <PolarRadiusAxis
          angle={90}
          domain={[Number(lowerScale) || 0, Number(upperScale)]}
          tick={{ fill: graphColor || '#9e9e9e', fontSize: 10 }}
          axisLine={false}
        />
        {momentNames.map((name, index) => {
          const color = MOMENT_COLORS[index % MOMENT_COLORS.length]
          return (
            <Radar
              key={name}
              name={name}
              dataKey={name}
              stroke={color}
              fill={color}
              fillOpacity={0.2}
              strokeWidth={2}
              dot
            />
          )
        })}
        <Tooltip content={<MatrixRadarTooltip />} />
        <Legend wrapperStyle={{ color: graphColor || undefined }} />
      </RadarChart>
    </ResponsiveContainer>
  )
}

export default MatrixRadarChart
